<template>
    <div class="ads-list">
        <!-- Notification emails settings -->
        <div class="notif-settings">
            <div class="notif-settings-header" @click="showNotifSettings = !showNotifSettings">
                <span class="notif-settings-title">
                    <i class="material-icons">email</i>
                    עדכונים במייל לכל המועמדויות
                </span>
                <i class="material-icons notif-arrow" :class="{ open: showNotifSettings }">expand_more</i>
            </div>
            <div v-if="showNotifSettings" class="notif-settings-body">
                <p class="notif-hint">כתובות המייל הבאות יקבלו עדכון בכל פעם שמועמד/ת שולח/ת טופס, לכל המשרות. סמן/י את התיבה כדי להפעיל שליחה.</p>
                <div v-for="(entry, i) in notificationEmails" :key="i" class="notif-row">
                    <label class="notif-checkbox-wrap" :title="entry.active ? 'פעיל' : 'לא פעיל'">
                        <input type="checkbox" v-model="notificationEmails[i].active" />
                        <span class="notif-checkbox"></span>
                    </label>
                    <input
                        v-model.trim="notificationEmails[i].email"
                        type="email"
                        placeholder="email@example.com"
                        dir="ltr"
                        class="notif-email-input"
                        :class="{ 'notif-email-inactive': !entry.active }"
                    />
                    <button class="notif-remove-btn" @click="notificationEmails.splice(i, 1)" title="הסר">✕</button>
                </div>
                <span v-if="notifError" class="notif-error">{{ notifError }}</span>
                <div class="notif-actions">
                    <button class="notif-add-btn" @click="notificationEmails.push({ email: '', active: true })">+ הוסף כתובת מייל</button>
                    <button class="notif-save-btn" @click="saveNotifEmails" :disabled="isSavingNotif">
                        <span v-if="isSavingNotif" class="spinner-sm"></span>
                        <span v-else>שמור</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Toolbar -->
        <div class="ads-toolbar">
            <h3 class="ads-toolbar-title">מודעות פורסמו ({{ ads.length }})</h3>
            <button class="new-ad-btn" @click="openCreate">
                <i class="material-icons">add</i>
                מודעה חדשה
            </button>
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="ads-loading">
            <span class="spinner"></span>
            <span>טוען מודעות...</span>
        </div>

        <!-- Empty -->
        <div v-else-if="!ads.length" class="ads-empty">
            <i class="material-icons">work_off</i>
            <p>אין מודעות עדיין. לחץ על "מודעה חדשה" כדי להתחיל.</p>
        </div>

        <!-- Grid of cards -->
        <div v-else class="ads-grid">
            <AdCard
                v-for="ad in ads"
                :key="ad.id"
                :ad="ad"
                :preview="true"
                :active="ad.active"
                @toggle-active="toggleActive(ad)"
                @edit="openEdit(ad)"
                @delete="handleDelete(ad)"
                @copy-link="copyLink(ad)"
            />
        </div>

        <!-- Editor modal -->
        <AdEditor
            v-if="showEditor"
            :ad="editingAd"
            @close="showEditor = false"
            @saved="onSaved"
        />
    </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { projectFirestore } from '@/firebase/config'
import { alert_confirm, slide_pop_successs, slide_pop_error } from '@/Methods/Msgs'
import AdCard from '@/components/Drushim/AdCard.vue'
import AdEditor from './AdEditor.vue'
import use_job_ads_storage from '@/Methods/use_job_ads_storage'

export default {
    name: 'AdsList',
    components: { AdCard, AdEditor },
    setup() {
        const { deleteFile } = use_job_ads_storage()
        const ads = ref([])
        const isLoading = ref(false)
        const showEditor = ref(false)
        const editingAd = ref(null)

        // Notification emails (global settings)
        const showNotifSettings = ref(false)
        const notificationEmails = ref([])
        const isSavingNotif = ref(false)
        const notifError = ref('')

        const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

        const fetchNotifEmails = async () => {
            try {
                const doc = await projectFirestore.collection('Utils').doc('JobAdsConfig').get()
                if (doc.exists && doc.data().notificationEmails) {
                    const raw = doc.data().notificationEmails
                    // Backward compatibility: convert plain strings to objects
                    notificationEmails.value = raw.map(entry =>
                        typeof entry === 'string'
                            ? { email: entry, active: true }
                            : { email: entry.email || '', active: entry.active !== false }
                    )
                }
            } catch { /* silent */ }
        }

        const saveNotifEmails = async () => {
            notifError.value = ''
            const filtered = notificationEmails.value.filter(e => e.email.trim())
            if (filtered.length === 0) {
                notifError.value = 'יש להוסיף לפחות כתובת מייל אחת'
                return
            }
            const invalid = filtered.filter(e => !isValidEmail(e.email))
            if (invalid.length > 0) {
                notifError.value = 'אחת או יותר מכתובות המייל אינן תקינות'
                return
            }
            const activeCount = filtered.filter(e => e.active).length
            if (activeCount === 0) {
                notifError.value = 'יש לסמן לפחות כתובת מייל אחת כפעילה'
                return
            }
            isSavingNotif.value = true
            try {
                await projectFirestore.collection('Utils').doc('JobAdsConfig').set(
                    { notificationEmails: filtered },
                    { merge: true }
                )
                notificationEmails.value = filtered
                slide_pop_successs('כתובות המייל נשמרו')
            } catch {
                slide_pop_error('שגיאה בשמירת הגדרות')
            } finally {
                isSavingNotif.value = false
            }
        }

        const fetchAds = async () => {
            isLoading.value = true
            try {
                const snap = await projectFirestore
                    .collection('JobAds')
                    .orderBy('createdAt', 'desc')
                    .get()
                ads.value = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
            } catch (err) {
                slide_pop_error('שגיאה בטעינת המודעות')
            } finally {
                isLoading.value = false
            }
        }

        const openCreate = () => {
            editingAd.value = null
            showEditor.value = true
        }

        const openEdit = (ad) => {
            editingAd.value = { ...ad }
            showEditor.value = true
        }

        const onSaved = () => {
            fetchAds()
        }

        const toggleActive = async (ad) => {
            try {
                const newActive = !ad.active
                await projectFirestore.collection('JobAds').doc(ad.id).update({ active: newActive })
                ad.active = newActive
            } catch {
                slide_pop_error('שגיאה בעדכון סטטוס')
            }
        }

        const copyLink = async (ad) => {
            const url = `${window.location.origin}/jobs?ad=${ad.id}`
            try {
                await navigator.clipboard.writeText(url)
                slide_pop_successs('הלינק הועתק!')
            } catch {
                slide_pop_error('שגיאה בהעתקת הלינק')
            }
        }

        const handleDelete = async (ad) => {
            const confirmed = await alert_confirm('האם למחוק את המודעה לצמיתות?')
            if (!confirmed.isConfirmed) return
            try {
                if (ad.image && ad.image.file_path) {
                    await deleteFile(ad.image.file_path)
                }
                await projectFirestore.collection('JobAds').doc(ad.id).delete()
                ads.value = ads.value.filter(a => a.id !== ad.id)
                slide_pop_successs('המודעה נמחקה')
            } catch {
                slide_pop_error('שגיאה במחיקה')
            }
        }

        onMounted(() => {
            fetchAds()
            fetchNotifEmails()
        })

        return {
            ads, isLoading, showEditor, editingAd, openCreate, openEdit, onSaved, toggleActive, handleDelete, copyLink,
            showNotifSettings, notificationEmails, isSavingNotif, notifError, saveNotifEmails
        }
    }
}
</script>

<style scoped>
.ads-list {
    padding: 20px;
    height: 100%;
    overflow-y: auto;
    box-sizing: border-box;
}

/* Notification settings panel */
.notif-settings {
    border: 1.5px solid #e0e0e0;
    border-radius: 12px;
    margin-bottom: 20px;
    background: #fff;
    overflow: hidden;
}
.notif-settings-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    cursor: pointer;
    user-select: none;
    transition: background 0.15s;
}
.notif-settings-header:hover {
    background: #fafafa;
}
.notif-settings-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 700;
    color: #222;
}
.notif-settings-title i {
    font-size: 20px;
    color: #1579F2;
}
.notif-arrow {
    font-size: 22px;
    color: #888;
    transition: transform 0.2s;
}
.notif-arrow.open {
    transform: rotate(180deg);
}
.notif-settings-body {
    padding: 0 16px 16px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.notif-hint {
    font-size: 13px;
    color: #888;
    margin: 12px 0 4px;
}
.notif-row {
    display: flex;
    gap: 8px;
    align-items: center;
}
.notif-checkbox-wrap {
    display: flex;
    align-items: center;
    cursor: pointer;
    flex-shrink: 0;
}
.notif-checkbox-wrap input[type="checkbox"] {
    display: none;
}
.notif-checkbox {
    width: 20px;
    height: 20px;
    border: 2px solid #ccc;
    border-radius: 4px;
    position: relative;
    transition: border-color 0.2s, background 0.2s;
}
.notif-checkbox-wrap input:checked + .notif-checkbox {
    background: #1579F2;
    border-color: #1579F2;
}
.notif-checkbox-wrap input:checked + .notif-checkbox::after {
    content: '';
    position: absolute;
    width: 5px;
    height: 10px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    top: 2px;
    left: 6px;
    transform: rotate(45deg);
}
.notif-email-inactive {
    opacity: 0.5;
}
.notif-error {
    font-size: 12px;
    color: #bb2429;
    padding-right: 4px;
}
.notif-email-input {
    flex: 1;
    padding: 10px 14px;
    border: 1.5px solid #ccc;
    border-radius: 20px;
    font-size: 14px;
    outline: none;
    font-family: inherit;
    transition: border-color 0.2s;
}
.notif-email-input:focus {
    border-color: #1579F2;
}
.notif-remove-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid #e0e0e0;
    background: #fafafa;
    cursor: pointer;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.2s;
}
.notif-remove-btn:hover {
    background: #fff0f0;
    border-color: #bb2429;
    color: #bb2429;
}
.notif-actions {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-top: 4px;
}
.notif-add-btn {
    background: none;
    border: 1.5px dashed #ccc;
    border-radius: 20px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #555;
    cursor: pointer;
    font-family: inherit;
    transition: border-color 0.2s, color 0.2s;
}
.notif-add-btn:hover {
    border-color: #1579F2;
    color: #1579F2;
}
.notif-save-btn {
    height: 36px;
    padding: 0 20px;
    background: #FEF200;
    color: #000;
    border: 1.5px solid #000;
    border-radius: 50px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: opacity 0.2s;
}
.notif-save-btn:disabled {
    opacity: 0.6;
    cursor: default;
}
.spinner-sm {
    width: 16px;
    height: 16px;
    border: 2px solid #aaa;
    border-top-color: #000;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    display: inline-block;
}

/* Toolbar */
.ads-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 10px;
}
.ads-toolbar-title {
    font-size: 18px;
    font-weight: 700;
    color: #222;
    margin: 0;
}
.new-ad-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 42px;
    padding: 0 20px;
    background: #FEF200;
    color: #000;
    border: 1.5px solid #000;
    border-radius: 50px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.1s;
    font-family: inherit;
}
.new-ad-btn i {
    font-size: 20px;
}
.new-ad-btn:hover {
    opacity: 0.85;
    transform: scale(1.02);
}

/* Loading */
.ads-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 60px;
    color: #888;
    font-size: 15px;
}

/* Empty */
.ads-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
    color: #aaa;
    gap: 12px;
    text-align: center;
}
.ads-empty i {
    font-size: 64px;
}
.ads-empty p {
    font-size: 15px;
    color: #888;
    max-width: 340px;
}

/* Grid */
.ads-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
}
/* Spinner */
.spinner {
    width: 24px;
    height: 24px;
    border: 3px solid #ddd;
    border-top-color: #1579F2;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    display: inline-block;
}
@keyframes spin {
    to { transform: rotate(360deg); }
}
</style>
